import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';

// Lib
import {
    agency_industry,
    business_type,
    company_industry,
    company_people_count,
    is_franchise
} from '@/lib/signup/userInfos/marketingOptions';

import FormFieldMessage from '@/components/base/FormFieldMessage';
import Input from '@/components/base/Input';
//Components
import Label from '@/components/base/Label';
import { Select } from '@/components/base/Select/SelectComponent';
import RadioButton from '@/components/composite/Radio/RadioButton';
import RadioImage from '@/components/composite/Radio/RadioImage';
// Types
import { UserInfosFormType } from '@/components/pages/signup/userInfos/UserInfosForm';

const SubStepThreeForm = () => {
    const { t } = useTranslation('signup_funnel');

    const {
        register,
        control,
        watch,
        setValue,
        clearErrors,
        formState: { errors }
    } = useFormContext<UserInfosFormType>();

    const handleBusinessTypeChange = (
        newValue: UserInfosFormType['business_type'],
        field: { onChange: (value: UserInfosFormType['business_type']) => void }
    ) => {
        field.onChange(newValue);
        setValue('company_people_count', newValue === 'independent_freelancer' ? '1' : null);
        setValue('company_industry', null);
        setValue('is_franchise', null);
        clearErrors(['company_people_count', 'company_industry', 'is_franchise']);
    };

    const [isFreelance, setIsFreelance] = useState(
        watch('business_type') === 'independent_freelancer'
    );

    useEffect(() => {
        if (watch('business_type') === 'independent_freelancer') {
            setIsFreelance(true);
        } else {
            setIsFreelance(false);
        }
    }, [watch('business_type')]);

    const isAgency = watch('business_type') === 'agency';

    return (
        <>
            {/* Company name */}
            <div className="flex w-full flex-col space-y-m xl:flex-row xl:space-x-3 xl:space-y-0">
                <div className="w-full xl:w-1/2">
                    <Label required htmlFor="company" fontType="input">
                        {t('user_infos.sub_step_3.company')}
                    </Label>
                    <Input
                        data-testid="company"
                        placeholder={t('user_infos.sub_step_3.company_placeholder')}
                        type="text"
                        id="company"
                        name="company"
                        error={errors?.company?.message}
                        {...register('company', {
                            required: t('user_infos.errors.error_company_required')
                        })}
                    />
                </div>
            </div>

            {/* Activity */}
            <div className="flex flex-col">
                <Label required fontType="section" className="mb-4 !w-auto">
                    {t('user_infos.sub_step_3.business_type.label')}
                </Label>
                <Controller
                    control={control}
                    name="business_type"
                    rules={{
                        required: t('user_infos.errors.error_business_type_required')
                    }}
                    render={({ field }) => (
                        <fieldset className="flex flex-wrap gap-1.5">
                            {business_type(t).map((opt) => (
                                <RadioButton
                                    key={opt.value}
                                    id={`company_type_${opt.value}`}
                                    name={field.name}
                                    value={opt.value}
                                    checked={field.value === opt.value}
                                    onChange={() => handleBusinessTypeChange(opt.value, field)}
                                    onBlur={field.onBlur}
                                    className="max-w-28 truncate md:max-w-fit lg:text-nowrap"
                                    data-testid={`company_type_${opt.value}`}>
                                    {opt.label}
                                </RadioButton>
                            ))}
                        </fieldset>
                    )}
                />
            </div>
            {errors?.business_type ? (
                <FormFieldMessage className="mt-2">
                    {errors.business_type.message ??
                        t('user_infos.errors.error_business_type_required')}
                </FormFieldMessage>
            ) : null}

            {/* Depending on the answers above, we will ask you the following questions */}
            <div
                className={classNames('flex flex-row flex-wrap gap-3', {
                    'xl:flex-nowrap': isAgency
                })}>
                {/* How many people work there? (Hidden and set to one by default for freelancers) */}
                {[
                    'agency',
                    'enterprise_brand_unique_location',
                    'ngo_or_npo_organization',
                    'independent_freelancer'
                ].includes(watch('business_type')) && (
                    <div className={classNames('w-full xl:w-1/2', { hidden: isFreelance })}>
                        <Label required fontType="section">
                            {t('user_infos.sub_step_3.company_people_count.label')}
                        </Label>
                        <Controller
                            control={control}
                            name="company_people_count"
                            rules={{
                                validate: (el) =>
                                    el != null ||
                                    t('user_infos.errors.error_company_people_count_required')
                            }}
                            render={({ field: { onChange, value } }) => (
                                <Select
                                    className="max-w-full"
                                    selected={
                                        isFreelance
                                            ? company_people_count(t).find(
                                                  (item) => item.value === '1'
                                              ) ?? {
                                                  label: t(
                                                      'user_infos.sub_step_3.company_people_count.placeholder'
                                                  ),
                                                  value: null
                                              }
                                            : company_people_count(t).find(
                                                  (item) => item.value === value
                                              ) ?? {
                                                  label: t(
                                                      'user_infos.sub_step_3.company_people_count.placeholder'
                                                  ),
                                                  value: null
                                              }
                                    }
                                    panelOptions={{
                                        placement: 'top',
                                        optionsWrapperClassName: '!max-h-44'
                                    }}
                                    buttonVariant="lightBorder"
                                    setSelected={(el) => {
                                        onChange(el.value);
                                    }}
                                    required={true}
                                    error={errors?.company_people_count?.message}
                                    options={company_people_count(t)}
                                />
                            )}
                        />
                    </div>
                )}

                {/* Agency details */}
                {['agency'].includes(watch('business_type')) && (
                    <>
                        <div className="w-full xl:w-1/2">
                            <Label required fontType="section">
                                {t('user_infos.sub_step_3.agency_industry.label')}
                            </Label>
                            <Controller
                                control={control}
                                name="company_industry"
                                rules={{
                                    validate: (el) =>
                                        el != null ||
                                        t('user_infos.errors.error_company_industry_required')
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        className="max-w-full"
                                        selected={
                                            agency_industry(t).find(
                                                (item) => item.value === value
                                            ) ?? {
                                                label: t(
                                                    'user_infos.sub_step_3.agency_industry.placeholder'
                                                ),
                                                value: null
                                            }
                                        }
                                        panelOptions={{
                                            placement: 'top',
                                            optionsWrapperClassName: '!max-h-44'
                                        }}
                                        buttonVariant="lightBorder"
                                        setSelected={(el) => {
                                            onChange(el.value);
                                        }}
                                        required={true}
                                        error={errors?.company_industry?.message}
                                        options={agency_industry(t)}
                                    />
                                )}
                            />
                        </div>

                        {errors?.company_industry ? (
                            <FormFieldMessage className="mt-2">
                                {errors.company_industry.message}
                            </FormFieldMessage>
                        ) : null}
                    </>
                )}

                {/* Is your business a multi-location company or a franchise? */}
                {['enterprise_brand_unique_location', 'enterprise_brand_multi_location'].includes(
                    watch('business_type')
                ) && (
                    <div className="w-full">
                        <Label required fontType="section" className="mb-4 !w-auto">
                            {t('user_infos.sub_step_3.is_franchise.label')}
                        </Label>
                        <Controller
                            control={control}
                            name="is_franchise"
                            rules={{
                                required: t('user_infos.errors.error_is_franchise_required')
                            }}
                            render={({ field }) => (
                                <fieldset className="flex flex-1 space-x-3">
                                    {is_franchise(t).map((opt) => (
                                        <RadioImage
                                            key={opt.value}
                                            id={`is_franchise_${opt.value}`}
                                            name={field.name}
                                            src={opt.src}
                                            label={opt.label}
                                            value={opt.value}
                                            checked={field.value === opt.value}
                                            onChange={() => field.onChange(opt.value)}
                                            onBlur={field.onBlur}
                                            withImgBackground={false}
                                            className="min-w-[calc(25%-0.375rem)]"
                                            data-testid={`is_franchise_${opt.value}`}
                                        />
                                    ))}
                                </fieldset>
                            )}
                        />
                    </div>
                )}

                {/* Industry details */}
                {[
                    'enterprise_brand_unique_location',
                    'enterprise_brand_multi_location',
                    'independent_freelancer'
                ].includes(watch('business_type')) && (
                    <div className="w-full">
                        <Label required fontType="section">
                            {t('user_infos.sub_step_3.company_industry.label')}
                        </Label>
                        <div className="w-full xl:w-1/2">
                            <Controller
                                control={control}
                                name="company_industry"
                                rules={{
                                    validate: (value) =>
                                        value != null ||
                                        t('user_infos.errors.error_company_industry_required')
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <Select
                                        className="max-w-full"
                                        selected={
                                            company_industry(t).find(
                                                (item) => item.value === value
                                            ) ?? {
                                                label: t(
                                                    'user_infos.sub_step_3.company_industry.placeholder'
                                                ),
                                                value: null
                                            }
                                        }
                                        panelOptions={{
                                            placement: 'top',
                                            optionsWrapperClassName: '!max-h-44'
                                        }}
                                        buttonVariant="lightBorder"
                                        setSelected={(el) => {
                                            onChange(el.value);
                                        }}
                                        required={true}
                                        error={errors?.company_industry?.message}
                                        options={company_industry(t)}
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SubStepThreeForm;
